import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  PromptEditor,
  VersionHistory,
  Tabs,
  StatusBadge,
  VersionBadge,
} from "../components.jsx";
import ProtocolLinker from "./ProtocolLinker.jsx";
import { useSubTenantEnv } from "./SubTenantEnvContext.jsx";
import { computeDiff } from "./diff-utils.js";
import { AuthoredView, CompiledView } from "../components/prompt-builder.jsx";
import { PromptTabWithSubtabs as StructuredPromptTab } from "./StructuredAgentEditor.jsx";

export default function SubTenantPromptEdit() {
  const { tid, sid, agent } = useParams();
  const { env: mode, isLive } = useSubTenantEnv();
  // Top-level tabs (universal structure):
  //   Configuration | Prompt | Agent Rules | History | Compare with Library
  // Configuration shows the same fields as the Library agent's Configuration
  // tab, but inheritance-driven fields (name, type, color, icon, specialties,
  // tools, triggers, protocol_directory) are read-only because they're
  // owned by the library and forked at sub-tenant creation. The Compare
  // with Library tab is sub-tenant-only.
  const [tab, setTab] = useState("configuration");
  // Sub-tab inside the Prompt tab: Structured | Combined (agent only) | Master Combined (LLM view)
  const [promptSubtab, setPromptSubtab] = useState("structured");
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [subTenant, setSubTenant] = useState(null);
  const [linkedProtocols, setLinkedProtocols] = useState([]);
  const [libraryPrompt, setLibraryPrompt] = useState(null);
  const [libraryAgent, setLibraryAgent] = useState(null); // full library agent doc — drives the Configuration tab (read-only)
  const [allTools, setAllTools] = useState([]); // tool catalog for tooltips on the Configuration tab
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Phase 3 — structured definition + compiled prompt fetched lazily
  // when the user opens the Structured / Combined tabs.
  const [compiled, setCompiled] = useState(null);
  const [compiledLoading, setCompiledLoading] = useState(false);
  const [compiledError, setCompiledError] = useState(null);

  // Structured definition for this agent — sub-tenant fork, falling back
  // to platform-agent-def. When present, the Prompt tab renders the
  // editable structured editor instead of the legacy prose editor.
  const [structuredDef, setStructuredDef] = useState(null);
  const [structuredDoc, setStructuredDoc] = useState(null); // sub-tenant doc (may be null if inherited)
  const [structuredHasOverride, setStructuredHasOverride] = useState(false);
  const [structuredRawJson, setStructuredRawJson] = useState("");
  const [structuredRawErr, setStructuredRawErr] = useState(null);
  const [structuredBusy, setStructuredBusy] = useState(false);

  // Lazy-load compiled prompt when the Prompt tab opens. Both subtabs
  // (structured + combined) consume the same payload from the
  // /compiled-prompt endpoint — fetched once, sliced two ways in render.
  useEffect(() => {
    if (tab !== "prompt") return;
    let cancelled = false;
    setCompiledLoading(true);
    setCompiledError(null);
    setCompiled(null);
    (async () => {
      try {
        const r = await fetch(
          `/api/tenants/${tid}/sub-tenants/${sid}/agents/${agent}/compiled-prompt?direction=inbound`,
          { credentials: "include" }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setCompiled(j.data);
      } catch (e) {
        if (!cancelled) setCompiledError(e.message || String(e));
      } finally {
        if (!cancelled) setCompiledLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, tid, sid, agent]);

  // Lazy-load the structured definition when the Prompt tab opens.
  // Try sub-tenant fork first, fall back to platform-agent-def.
  useEffect(() => {
    if (tab !== "prompt") return;
    let cancelled = false;
    (async () => {
      let def = null;
      let doc = null;
      let hasOverride = false;
      try {
        const r = await api.getSubTenantAgentDef(tid, sid, agent, mode);
        if (r?.data?.definition) {
          def = r.data.definition;
          doc = r.data;
          hasOverride = true;
        }
      } catch { /* fall through to platform */ }
      if (!def) {
        try {
          const r = await api.getPlatformAgentDef(agent, "live");
          if (r?.data?.definition) {
            def = r.data.definition;
            doc = r.data;
          }
        } catch { /* no structured def — keep prose */ }
      }
      if (!cancelled) {
        setStructuredDef(def);
        setStructuredDoc(doc);
        setStructuredHasOverride(hasOverride);
        setStructuredRawJson(def ? JSON.stringify(def, null, 2) : "");
      }
    })();
    return () => { cancelled = true; };
  }, [tab, tid, sid, agent, mode]);

  // updateField mutates the in-memory definition; user must click Save
  // Draft to persist via writeSubTenantAgentDef (forks if inherited).
  const updateStructuredField = useCallback((path, value) => {
    setStructuredDef((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      let cursor = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (cursor[path[i]] == null) cursor[path[i]] = {};
        cursor = cursor[path[i]];
      }
      cursor[path[path.length - 1]] = value;
      setStructuredRawJson(JSON.stringify(next, null, 2));
      return next;
    });
  }, []);

  const saveStructured = useCallback(async () => {
    if (!structuredDef) return;
    setStructuredBusy(true);
    setError(null);
    try {
      await api.writeSubTenantAgentDef(tid, sid, agent, {
        definition: structuredDef,
        author: "admin",
        commit_msg: `Edit structured definition for ${agent}`,
      });
      // Reload structured + compiled so views reflect the new save.
      const r = await api.getSubTenantAgentDef(tid, sid, agent, mode);
      if (r?.data?.definition) {
        setStructuredDef(r.data.definition);
        setStructuredDoc(r.data);
        setStructuredHasOverride(true);
        setStructuredRawJson(JSON.stringify(r.data.definition, null, 2));
      }
    } catch (err) {
      setError(err);
    } finally {
      setStructuredBusy(false);
    }
  }, [structuredDef, tid, sid, agent, mode]);

  const saveStructuredRaw = useCallback(async () => {
    setStructuredRawErr(null);
    let parsed;
    try { parsed = JSON.parse(structuredRawJson); }
    catch (err) { setStructuredRawErr(`Invalid JSON: ${err.message}`); return; }
    setStructuredBusy(true);
    try {
      await api.writeSubTenantAgentDef(tid, sid, agent, {
        definition: parsed,
        author: "admin",
        commit_msg: `Edit structured definition for ${agent} (raw JSON)`,
      });
      const r = await api.getSubTenantAgentDef(tid, sid, agent, mode);
      if (r?.data?.definition) {
        setStructuredDef(r.data.definition);
        setStructuredDoc(r.data);
        setStructuredHasOverride(true);
        setStructuredRawJson(JSON.stringify(r.data.definition, null, 2));
      }
    } catch (err) {
      setError(err);
    } finally {
      setStructuredBusy(false);
    }
  }, [structuredRawJson, tid, sid, agent, mode]);

  const apiCompileForStructured = useCallback((direction) => {
    return api.getSubTenantAgentDefCompiledPrompt(tid, sid, agent, direction);
  }, [tid, sid, agent]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docRes, histRes, subRes, linksRes] = await Promise.all([
        api.getSubTenantPrompt(tid, sid, agent, mode),
        api.getSubTenantPromptHistory(tid, sid, agent),
        api.getSubTenant(tid, sid),
        api.getSubTenantAgentLinkedProtocols(tid, sid, agent),
      ]);
      setDoc(docRes.data);
      setHistory(histRes.data || []);
      setSubTenant(subRes.data);
      setLinkedProtocols(linksRes.data?.linked_protocols || []);

      // Fetch the library live version for diffing. The specialty
      // comes from the sub-tenant's `specialty` field, and the agent
      // key is `seeded_from.key` (or falls back to the route param).
      const specialty = subRes.data?.specialty;
      const sourceKey = docRes.data?.seeded_from?.key || agent;
      if (specialty) {
        try {
          const libRes = await api.getSpecialtyAgent(
            specialty,
            sourceKey,
            "live"
          );
          setLibraryPrompt(libRes.data?.content || null);
        } catch {
          setLibraryPrompt(null);
        }
      }

      // Also fetch the FULL library agent doc (config + tools + triggers
      // + specialties etc.) so the Configuration tab can render the same
      // shape as the Library page, read-only. This lookup uses the
      // multi-specialty agent endpoint (sourceKey is the agent _id).
      try {
        const agentRes = await api.getAgent(sourceKey, "live");
        setLibraryAgent(agentRes.data || null);
      } catch {
        setLibraryAgent(null);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid, agent, mode]);

  useEffect(() => {
    load();
  }, [load]);

  // Background-load the tool catalog once so the Configuration tab can
  // show tool descriptions next to the inherited tool list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.listTools().catch(() => ({ data: [] }));
        if (!cancelled) setAllTools(r.data || []);
      } catch {
        // ignore — tool catalog is informational only
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSaveLinkedProtocols = async (links) => {
    setBusy(true);
    setError(null);
    try {
      await api.setSubTenantAgentLinkedProtocols(tid, sid, agent, links);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (content, writeMode) => {
    setBusy(true);
    setError(null);
    try {
      await api.writeSubTenantPrompt(tid, sid, agent, {
        content,
        mode: writeMode,
        base_version: doc?.version ?? null,
        author: "admin",
        commit_msg: `Edit from admin UI (${writeMode})`,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm("Publish current draft to Live? This will apply to real callers immediately.")) return;
    setBusy(true);
    try {
      await api.publishSubTenantPrompt(tid, sid, agent);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!window.confirm("Discard current draft and revert to Live?")) return;
    setBusy(true);
    try {
      await api.discardSubTenantPromptDraft(tid, sid, agent);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const isOverridden = doc && (doc.version || 1) > 1;
  const envPrefix = `/tenants/${tid}/sub-tenants/${sid}/${mode}`;

  return (
    <>
      <PageHeader
        title={doc?.metadata?.name || agent}
        subtitle={`${isLive ? "Live (read-only)" : "Draft"} · ${
          isOverridden ? "Overridden" : "Inherited from library"
        }`}
        breadcrumbs={[
          { label: "Tenants", to: "/tenants" },
          { label: tid, to: `/tenants/${tid}` },
          { label: sid, to: `${envPrefix}` },
          { label: agent },
        ]}
        actions={
          doc && (
            <>
              <VersionBadge version={doc.version} />
              <StatusBadge doc={doc} />
            </>
          )
        }
      />

      <Tabs
        tabs={[
          { id: "configuration", label: "Configuration" },
          { id: "prompt", label: "Prompt" },
          { id: "protocols", label: `Agent Rules (${linkedProtocols.length})` },
          { id: "history", label: `History (${history.length})` },
          { id: "diff", label: "Compare with Library" },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="tab-content">
      <ErrorBanner error={error} />

      {loading ? (
        <Loading />
      ) : tab === "configuration" ? (
        <SubTenantAgentConfiguration
          libraryAgent={libraryAgent}
          allTools={allTools}
          subTenant={subTenant}
          doc={doc}
          agentId={agent}
        />
      ) : tab === "prompt" ? (
        structuredDef ? (
          <>
            <div style={{
              display: "flex", alignItems: "center", gap: 12, marginBottom: 12,
              padding: "8px 12px", background: "#eef3fa",
              border: "1px solid #cfd8e3", borderRadius: 6, fontSize: 12, color: "#2E4F72",
            }}>
              <strong>Structured editor</strong>
              <span>
                {structuredHasOverride
                  ? "Sub-tenant override of platform agent definition."
                  : "Inherited from platform agent definition. Saving will fork into a sub-tenant override."}
              </span>
              <div style={{ flex: 1 }} />
              {!isLive && (
                <button className="btn primary sm" onClick={saveStructured} disabled={structuredBusy}>
                  {structuredBusy ? "Saving..." : "Save Draft"}
                </button>
              )}
            </div>
            <StructuredPromptTab
              definition={structuredDef}
              updateField={updateStructuredField}
              readOnly={isLive}
              rawJson={structuredRawJson}
              setRawJson={setStructuredRawJson}
              rawJsonError={structuredRawErr}
              setRawJsonError={setStructuredRawErr}
              saveRaw={saveStructuredRaw}
              busy={structuredBusy}
              apiCompile={apiCompileForStructured}
            />
          </>
        ) : (
          <PromptTabWithSubtabs
            subtab={promptSubtab}
            onChangeSubtab={setPromptSubtab}
            compiled={compiled}
            loading={compiledLoading}
            error={compiledError}
            agentId={agent}
          />
        )
      ) : tab === "diff" ? (
        <PromptDiffViewer
          libraryContent={libraryPrompt}
          subTenantContent={doc?.content}
          specialty={subTenant?.specialty}
          agentKey={agent}
        />
      ) : tab === "protocols" ? (
        <>
          <p className="muted small mb-16">
            Agent rules linked here override the specialty-template defaults
            for this specific sub-tenant's agent. Changes do not affect other
            sub-tenants that share the same specialty.
          </p>
          {subTenant?.specialty ? (
            <ProtocolLinker
              specialty={subTenant.specialty}
              currentLinks={linkedProtocols}
              onSave={handleSaveLinkedProtocols}
              busy={busy}
            />
          ) : (
            <div className="empty-state">
              <h3>Sub-tenant specialty unknown</h3>
              <p>Cannot load agent rules without knowing the sub-tenant's specialty.</p>
            </div>
          )}
        </>
      ) : (
        <div className="card">
          <VersionHistory versions={history} />
        </div>
      )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Configuration tab — read-only mirror of the Library agent's
// Basic Information / Assigned Tools / Triggers / Specialties cards.
// All fields are inheritance-driven (forked from the library at sub-tenant
// creation), so we deliberately render them as static values rather than
// editable inputs. The shape matches the Library page so reviewers can
// scan the same fields in the same positions on either side.
// ─────────────────────────────────────────────────────────────────────
function SubTenantAgentConfiguration({ libraryAgent, allTools, subTenant, doc, agentId }) {
  const toolMap = React.useMemo(() => {
    const m = new Map();
    for (const t of allTools || []) m.set(t._id, t);
    return m;
  }, [allTools]);

  if (!libraryAgent && !doc) {
    return (
      <div className="empty-state">
        <h3>No configuration to show</h3>
        <p>The library agent record could not be loaded for {agentId}.</p>
      </div>
    );
  }

  // Prefer values from the library agent (the inheritance source).
  // Fall back to the sub-tenant doc's metadata if library lookup failed.
  const name = libraryAgent?.name || doc?.metadata?.name || agentId;
  const description = libraryAgent?.description || doc?.metadata?.description || "";
  const agentType = libraryAgent?.agent_type || "sub_agent";
  const color = libraryAgent?.color || "#455A64";
  const icon = libraryAgent?.icon || "";
  const enabled = libraryAgent?.enabled !== false;
  const protocolDirectory = libraryAgent?.protocol_directory || "";
  const specialties = libraryAgent?.specialties || (subTenant?.specialty ? [subTenant.specialty] : []);
  const tools = libraryAgent?.tools || [];
  const triggers = libraryAgent?.triggers || [];

  const ReadOnlyField = ({ label, value, multiline = false, hint = null, mono = false }) => (
    <div className="form-field">
      <label>{label}</label>
      <div style={{
        padding: "8px 10px",
        background: "#f6f7f9",
        border: "1px solid #e0e3e8",
        borderRadius: 4,
        fontSize: 13,
        fontFamily: mono ? "var(--font-mono)" : "inherit",
        color: "#222",
        whiteSpace: multiline ? "pre-wrap" : "normal",
        minHeight: 32,
      }}>
        {value || <span className="muted">—</span>}
      </div>
      {hint && <div className="help">{hint}</div>}
    </div>
  );

  const InheritanceBanner = () => (
    <div style={{
      padding: "10px 14px",
      background: "#eef3fa",
      border: "1px solid #cfd8e3",
      borderRadius: 6,
      fontSize: 12,
      color: "#2E4F72",
      marginBottom: 16,
      lineHeight: 1.55,
    }}>
      <strong>Inherited from library</strong> — every field below is forked
      from the library agent at sub-tenant creation. Edit these in the
      Library page; they are read-only here. The Prompt and Agent Rules tabs
      are the per-sub-tenant overrides.
    </div>
  );

  return (
    <div className="agent-config-grid">
      <div>
        {/* LEFT — Basic Information mirror */}
        <div className="card">
          <div className="card-header">
            <h3>Basic Information</h3>
            <span className="muted small" style={{ marginLeft: 8 }}>read-only · inherited</span>
          </div>
          <InheritanceBanner />

          <ReadOnlyField label="Name" value={name} />
          <ReadOnlyField label="Description" value={description} multiline />

          <div className="field-row">
            <div className="form-field" style={{ flex: 1 }}>
              <label>Type</label>
              <div style={{ padding: "8px 10px", background: "#f6f7f9", border: "1px solid #e0e3e8", borderRadius: 4, fontSize: 13 }}>
                {agentType}
              </div>
            </div>
            <div className="form-field" style={{ width: 80 }}>
              <label>Color</label>
              <div style={{
                width: 40, height: 32, borderRadius: 4,
                border: "1px solid #e0e3e8", background: color,
              }} title={color} />
            </div>
            <div className="form-field" style={{ width: 70 }}>
              <label>Icon</label>
              <div style={{
                padding: "8px 10px", background: "#f6f7f9",
                border: "1px solid #e0e3e8", borderRadius: 4,
                fontSize: 14, textAlign: "center", fontWeight: 600,
              }}>
                {icon || <span className="muted">—</span>}
              </div>
            </div>
            <div className="form-field" style={{ width: 80 }}>
              <label>Enabled</label>
              <div style={{
                padding: "8px 10px", background: "#f6f7f9",
                border: "1px solid #e0e3e8", borderRadius: 4,
                fontSize: 13, textAlign: "center",
                color: enabled ? "#2e7d32" : "#c62828",
                fontWeight: 600,
              }}>
                {enabled ? "✓ Yes" : "✗ No"}
              </div>
            </div>
          </div>

          <ReadOnlyField
            label="Protocol Directory"
            value={protocolDirectory}
            mono
            hint="Directory of protocol files loaded at runtime via tool calls."
          />

          <div className="form-field">
            <label>Specialty / Agent Group</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {specialties.length === 0 ? (
                <span className="muted small">(none)</span>
              ) : (
                specialties.map((s) => (
                  <span key={s} className="badge badge-accent" style={{ fontFamily: "var(--font-mono)" }}>
                    {s}
                  </span>
                ))
              )}
            </div>
            <div className="help">
              Group membership lives in the library — an agent can belong to
              one or more groups.
            </div>
          </div>
        </div>
      </div>

      <div>
        {/* RIGHT — Assigned Tools + Triggers, mirrors */}
        <div className="card">
          <div className="card-header">
            <h3>
              Assigned Tools{" "}
              <span className="muted small" style={{ marginLeft: 6, fontWeight: 400 }}>
                {tools.length}
              </span>
            </h3>
            <span className="muted small">read-only · inherited</span>
          </div>
          <div className="agent-tool-list">
            {tools.length === 0 ? (
              <div className="muted small" style={{ padding: "12px 0" }}>
                No tools assigned in the library.
              </div>
            ) : (
              tools.map((tid) => {
                const def = toolMap.get(tid);
                return (
                  <div key={tid} className="agent-tool-row">
                    <div className="agent-tool-info">
                      <div className="mono small" style={{ fontWeight: 600 }}>
                        {tid}
                      </div>
                      <div className="muted small">
                        {def?.description || (
                          <span className="subtle">Not in catalog.</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <h3>
              Triggers{" "}
              <span className="muted small" style={{ marginLeft: 6, fontWeight: 400 }}>
                {triggers.length}
              </span>
            </h3>
            <span className="muted small">read-only · inherited</span>
          </div>
          <p className="muted small mb-16">
            Keywords that route conversations to this agent.
          </p>
          <div className="trigger-pills">
            {triggers.length === 0 ? (
              <span className="subtle small">No triggers configured</span>
            ) : (
              triggers.map((t) => (
                <span key={t} className="trigger-pill">{t}</span>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Prompt diff viewer ─────────────────────────────────────────────
//
// Simple line-by-line diff between the library live prompt and the
// sub-tenant's current prompt. Lines are compared sequentially:
//   - Matching lines → grey (unchanged)
//   - Lines only in library → red background (removed)
//   - Lines only in sub-tenant → green background (added)
//
// Uses a basic LCS-based diff algorithm to align lines correctly.

// ─────────────────────────────────────────────────────────────────────
// PromptTabWithSubtabs — owns the Prompt tab body. Renders a subtab
// strip (Structured / Combined) plus the matching pane below.
// Structured = the authored, sectioned view (read-only for now;
// editable in subsequent phases). Combined = the compiled prompt the
// LLM actually receives.
// ─────────────────────────────────────────────────────────────────────
function PromptTabWithSubtabs({ subtab, onChangeSubtab, compiled, loading, error, agentId }) {
  return (
    <div>
      <div style={{
        display: "flex", gap: 6,
        padding: "8px 0",
        borderBottom: "1px solid #e5e5e5",
        marginBottom: 16,
        flexWrap: "wrap",
      }}>
        <button
          className="btn btn-sm"
          onClick={() => onChangeSubtab("structured")}
          style={subtab === "structured" ? { background: "#2E4F72", color: "#fff" } : {}}
        >
          Structured editor
        </button>
        <button
          className="btn btn-sm"
          onClick={() => onChangeSubtab("combined")}
          style={subtab === "combined" ? { background: "#2E4F72", color: "#fff" } : {}}
          title="The agent's content (persona + rules + steps + exit) as flat prose, without framework or universal rules. Useful for reviewing this agent's contribution in isolation."
        >
          Combined (agent only)
        </button>
        <button
          className="btn btn-sm"
          onClick={() => onChangeSubtab("master")}
          style={subtab === "master" ? { background: "#2E4F72", color: "#fff" } : {}}
          title="The whole prompt the LLM receives — global universal rules + framework navigation + this agent's content, merged and renumbered."
        >
          Master Combined (LLM view)
        </button>
        <div style={{ flex: 1 }} />
        <span className="muted small" style={{ alignSelf: "center" }}>
          {subtab === "structured" && "Sectioned, table-based authored view"}
          {subtab === "combined" && "This agent's content only — no framework rules"}
          {subtab === "master" && "Universal + framework + agent — what the LLM actually receives"}
        </span>
      </div>
      <PromptBuilderPane
        mode={subtab}
        compiled={compiled}
        loading={loading}
        error={error}
        agentId={agentId}
      />
    </div>
  );
}

function PromptBuilderPane({ mode, compiled, loading, error }) {
  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;
  if (!compiled) return <div className="muted">Loading prompt builder…</div>;

  if (compiled.source === "prose") {
    return (
      <div style={{
        padding: 14,
        background: "#fff8e1",
        border: "1px solid #ffe0a3",
        borderRadius: 6,
        fontSize: 13, color: "#5b4400",
      }}>
        <strong>This agent uses the legacy prose prompt path.</strong>
        <div style={{ marginTop: 6 }}>{compiled.message}</div>
        {compiled.available_structured_agents?.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12 }}>
            Currently migrated to structured authoring:{" "}
            <strong>{compiled.available_structured_agents.join(", ")}</strong>
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 12 }}>
          Migrating this agent to structured authoring is the next step.
          Until then this page can't render Structured / Combined views for it.
        </div>
      </div>
    );
  }

  if (mode === "structured") return <AuthoredView definition={compiled.definition} />;
  if (mode === "combined") {
    return (
      <CompiledView
        prompt={compiled.prompt_agent_only}
        ruleMap={compiled.rule_map_agent_only}
        title="Agent contribution only (no framework rules)"
      />
    );
  }
  // master
  return (
    <CompiledView
      prompt={compiled.prompt}
      ruleMap={compiled.rule_map}
      title="Full prompt sent to LLM (universal + framework + agent)"
    />
  );
}

function PromptDiffViewer({
  libraryContent,
  subTenantContent,
  specialty,
  agentKey,
}) {
  if (!libraryContent && !subTenantContent) {
    return (
      <div className="empty-state">
        <h3>No content to compare</h3>
        <p>Neither the library nor the sub-tenant has a prompt for this agent.</p>
      </div>
    );
  }

  const libLines = (libraryContent || "").split("\n");
  const subLines = (subTenantContent || "").split("\n");

  // Simple diff: compute edit script via LCS
  const diff = computeDiff(libLines, subLines);

  const identical = diff.every((d) => d.type === "equal");

  return (
    <>
      <div
        className="mb-16"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <span className="muted small">
            Comparing:{" "}
            <strong>Library Live</strong> ({specialty}/{agentKey}) vs{" "}
            <strong>Sub-tenant</strong> prompt
          </span>
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          <span style={{ fontSize: 12 }}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: "#fdd",
                border: "1px solid #faa",
                borderRadius: 2,
                marginRight: 4,
                verticalAlign: "middle",
              }}
            />
            Removed from library
          </span>
          <span style={{ fontSize: 12 }}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: "#dfd",
                border: "1px solid #afa",
                borderRadius: 2,
                marginRight: 4,
                verticalAlign: "middle",
              }}
            />
            Added in sub-tenant
          </span>
        </div>
      </div>

      {identical ? (
        <div
          className="card"
          style={{
            background: "#f0fff4",
            borderColor: "#a3e4a5",
            textAlign: "center",
            padding: 24,
          }}
        >
          <strong>No differences.</strong> The sub-tenant prompt matches
          the library live version exactly.
        </div>
      ) : (
        <div
          className="card"
          style={{
            padding: 0,
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            lineHeight: 1.6,
            overflow: "auto",
            maxHeight: "70vh",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {diff.map((line, i) => {
                const bg =
                  line.type === "removed"
                    ? "#fdd"
                    : line.type === "added"
                    ? "#dfd"
                    : "transparent";
                const prefix =
                  line.type === "removed"
                    ? "−"
                    : line.type === "added"
                    ? "+"
                    : " ";
                const color =
                  line.type === "removed"
                    ? "#900"
                    : line.type === "added"
                    ? "#060"
                    : "#444";
                return (
                  <tr key={i}>
                    <td
                      style={{
                        width: 40,
                        textAlign: "right",
                        padding: "0 8px",
                        color: "#999",
                        background: bg,
                        borderRight: "1px solid #ddd",
                        userSelect: "none",
                      }}
                    >
                      {line.lineNum ?? ""}
                    </td>
                    <td
                      style={{
                        width: 18,
                        textAlign: "center",
                        background: bg,
                        color,
                        fontWeight: 700,
                        userSelect: "none",
                      }}
                    >
                      {prefix}
                    </td>
                    <td
                      style={{
                        padding: "0 8px",
                        background: bg,
                        color,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {line.text}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// computeDiff is now imported from ./diff-utils.js
